import classnames from "classnames";
import { filterPathIntersectionCached, sortForSortierung } from "oc-app/metadata/GraphMetadata";
import * as Commons from "oc-commons/Commons";
import Select, { components } from "react-select";
import { MAuswahl, MAuswahlwert, MObjekt } from "../../metamodell";
import { PAuswahlwert } from "../../metamodell_paths";
import { findAuswahl } from "../arrangement/elementFunctions";
import { Image } from "./Image";
import { Radios } from "./Radios";
import React, { useRef, useState } from "react";
import { Joyrider } from "../commons/Joyrider";
import { Togglebutton } from "./Togglebutton";

export interface Option {
  label: string | JSX.Element;
  value: string;
  wert?: MAuswahlwert;
  isDisabled?: boolean;
  ariaLabel?: string;
}

export function HilfeIcon({ Text, Bild }: { Text?: string; Bild?: string }) {
  if (Commons.isEmpty(Text) && Commons.isEmpty(Bild)) {
    return null;
  }
  const ref = useRef(null);
  const [stepIndex, setStepIndex] = useState(-1);
  return (
    <>
      <div
        ref={ref}
        className="icon-info-circle info-tooltip-button"
        role="button"
        aria-label={I18N("aria::Hilfe anzeigen")}
        aria-expanded={stepIndex >= 0}
        onClick={(e) => {
          e.stopPropagation();
          setStepIndex(0);
        }}
      ></div>
      <Joyrider
        steps={[
          {
            target: ref.current,
            placement: "right",
            content: (
              <div className="auswahl-info flex-row flex-center" role="tooltip" aria-live="polite">
                <div className="text" dangerouslySetInnerHTML={{ __html: Text }}></div>
                <Image url={Bild} />
              </div>
            ),
            disableBeacon: true,
          },
        ]}
        width="auto"
        disableOverlay={true}
        stepIndex={stepIndex}
        setStepIndex={setStepIndex}
        disableScrolling={true}
        disableScrollParentFix={true}
        scrollDuration={0}
      />
    </>
  );
}

export type AuswahlAccessorType = {
  key: string;
  auswahl: MAuswahl;
  auswahlwert: MAuswahlwert;
  möglicheAuswahlwerte: MAuswahlwert[];
  setter: (value: MAuswahlwert) => void;
};

export function AuswahlAccessor({ accessorKey, ...props }: { accessorKey: string }) {
  if (APP.arrangement.auswahlAccessoren[accessorKey] == null) {
    debugger;
  }
  const { auswahl, auswahlwert, möglicheAuswahlwerte, setter } = APP.arrangement.auswahlAccessoren[
    accessorKey
  ] as AuswahlAccessorType;

  return (
    <Auswahl
      {...props}
      auswahl={auswahl}
      value={auswahlwert}
      auswahlwerte={möglicheAuswahlwerte}
      hideIfSingleValue={true}
      onChange={(v) => {
        setter(v);
        logUserAuswahl(v);
      }}
    />
  );
}

export function findAuswahlwerte({
  auswahl,
  Key,
  DetailSerien,
  Workflow,
  auswahlwerte,
  ungültigeKeys,
}: Partial<{
  auswahl: MAuswahl;
  Key: string;
  DetailSerien: MObjekt[];
  Workflow: string;
  auswahlwerte: MAuswahlwert[];
  ungültigeKeys: string[];
}>) {
  auswahl = auswahl ?? findAuswahl(Key, DetailSerien, Workflow);
  if (auswahl == null) {
    console.error("auswahl darf nicht null sein", { auswahl, Key, DetailSerien, Workflow });
    debugger;
  }
  // filter statt filterPathIntersection, weil Auswahl.Key kein Primary Key ist wegen Workflow und Detailserie, und die Graphstruktur an dieser Stelle versagt
  auswahlwerte = sortForSortierung(
    (auswahlwerte ?? APP.metadata.Auswahlwert.filter((w) => w.Auswahl.Key == auswahl.Key)).filter(
      (a) => !a.Gesperrt && !ungültigeKeys?.includes(a.Key)
    )
  );
  return { auswahl, auswahlwerte };
}

export function Auswahl({
  DetailSerien = undefined,
  Workflow = undefined,
  Key = undefined,
  title = undefined,
  customClassPortal = undefined,
  customLabel = undefined,
  value,
  onChange,
  type = "select",
  small = false,
  nullable = false,
  showHeadline = true,
  placeholder = undefined,
  auswahl = undefined,
  auswahlwerte = undefined,
  hideIfSingleValue = false,
  disableUnzulässigeAuswahlwerte = false,
  pathToZulässigeAuswahlwerte = [],
  ungültigeKeys = [],
}: {
  DetailSerien?: MObjekt[];
  Workflow?: string;
  Key?: string;
  title?: string;
  customClassPortal?: {};
  customLabel?: any;
  value?: MAuswahlwert;
  onChange?: (value: MAuswahlwert) => void;
  type?: "select" | "radio" | "toggle";
  small?: boolean;
  nullable?: boolean;
  showHeadline?: boolean;
  placeholder?: string | React.ReactNode;
  auswahl?: MAuswahl;
  auswahlwerte?: MAuswahlwert[];
  hideIfSingleValue?: boolean;
  disableUnzulässigeAuswahlwerte?: boolean;
  pathToZulässigeAuswahlwerte?: any[];
  ungültigeKeys?: any[];
}) {
  ({ auswahl, auswahlwerte } = findAuswahlwerte({
    auswahl,
    Key,
    DetailSerien,
    Workflow,
    auswahlwerte,
    ungültigeKeys,
  }));

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const liveRegionRef = useRef<HTMLDivElement>(null);

  const zulässigeAuswahlwerte =
    pathToZulässigeAuswahlwerte.length > 0
      ? (filterPathIntersectionCached("Auswahl.tsx:129")(
          auswahlwerte,
          (a: PAuswahlwert) => a,
          // @ts-ignore
          ...pathToZulässigeAuswahlwerte
        ) as MAuswahlwert[])
      : auswahlwerte;

  if (auswahlwerte.length == 0) {
    return null;
  }
  const options: Option[] = auswahlwerte
    .filter((a) => zulässigeAuswahlwerte.includes(a) || disableUnzulässigeAuswahlwerte)
    .map((w) => {
      const textContent = Commons.isEmpty(w.Bezeichnung) ? w.Name : w.Bezeichnung;
      const plainText = textContent.replace(/<[^>]*>/g, "").trim();

      return {
        value: w.Key,
        wert: w,
        isDisabled: !zulässigeAuswahlwerte.includes(w),
        ariaLabel: w.AriaText || plainText,
        label: customLabel ? (
          customLabel(w)
        ) : (
          <div className="dropdown-entry" data-value={w.Key}>
            {(!Commons.isEmpty(w.Auswahlbild) || !Commons.isEmpty(w.Thumbnail)) && (
              <Image url={w.Thumbnail || w.Auswahlbild} />
            )}
            <div
              className={classnames(
                "text",
                (!Commons.isEmpty(w.Auswahlbild) || !Commons.isEmpty(w.Thumbnail)) && "with-image"
              )}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: textContent,
                }}
              ></span>
            </div>
          </div>
        ),
      };
    });

  if (nullable) {
    options.unshift({ value: null, label: "-" });
  }

  const enabledOptions = options.filter((o) => !o.isDisabled);
  if (enabledOptions.length == 0 || (enabledOptions.length == 1 && hideIfSingleValue)) {
    return null;
  }
  const onChangeLogged = (wert: Option) => {
    const auswahlwert: MAuswahlwert = wert?.wert;
    onChange(auswahlwert);
    logUserAuswahl(auswahlwert);
  };

  const option = options.find((w) => w.wert == value) || null;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !menuIsOpen) {
      setMenuIsOpen(true);
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      // aria-label aus option extrahieren und in custom live region vorlesen lassen
      setTimeout(() => {
        const focusedElement = document.querySelector('[class*="my-select__option--is-focused"]');
        if (focusedElement) {
          const dataValue = focusedElement.getAttribute("data-value");
          const focusedOption = options.find((opt) => opt.value === dataValue);
          if (focusedOption?.ariaLabel && liveRegionRef.current) {
            liveRegionRef.current.textContent = focusedOption.ariaLabel;
          }
        }
      }, 50);
    }
  };

  // benutzerdefinierte Option-Komponente für Barrierefreiheit (aria-label)
  const CustomOption = (props: any) => {
    const { data, innerProps } = props;
    return (
      <components.Option
        {...props}
        innerProps={{
          ...innerProps,
          "data-value": data.value,
          "aria-label": data.ariaLabel || data.value,
        }}
      >
        {props.children}
      </components.Option>
    );
  };

  return (
    <>
      {/* Screenreader Live-Region */}
      <div
        ref={liveRegionRef}
        role="status"
        aria-live="assertive"
        aria-atomic="true"
        style={{
          position: "absolute",
          left: "-10000px",
          width: "1px",
          height: "1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
        }}
      >
        {/* Initiales Leerzeichen ist notwendig, damit die Region im DOM existiert */}
        &nbsp;
      </div>

      <div className="Auswahl" data-select-key={auswahl.Key} data-value={value?.Key} role="group">
        <div className="Headline">
          {showHeadline && (
            <>
              <span
                id={`auswahl-label-${auswahl.Key}`}
                dangerouslySetInnerHTML={{
                  __html: title ?? (small ? auswahl.Key : auswahl?.Text),
                }}
              ></span>
              <HilfeIcon Text={auswahl?.Hilfstext} Bild={auswahl?.Bild} />
            </>
          )}
        </div>

        <div className="select-container">
          {options.length == 1 ? (
            options[0].label
          ) : type === "radio" ? (
            <Radios name={Key} value={option} options={options} onChange={onChangeLogged} />
          ) : type === "toggle" ? (
            <Togglebutton name={Key} value={option} options={options} onChange={onChangeLogged} />
          ) : (
            <Select
              id={Key}
              aria-label={auswahl.AriaText || title || auswahl?.Text || Key}
              classNamePrefix={(customClassPortal ?? "") + " my-select"}
              menuPlacement="auto"
              value={option}
              options={options}
              onChange={onChangeLogged}
              menuIsOpen={menuIsOpen}
              onMenuOpen={() => setMenuIsOpen(true)}
              onMenuClose={() => setMenuIsOpen(false)}
              onKeyDown={handleKeyDown}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              placeholder={placeholder}
              components={{ Option: CustomOption }}
              formatOptionLabel={(option: Option) => option.label}
              getOptionLabel={(option: Option) =>
                option.ariaLabel || (typeof option.label === "string" ? option.label : option.value)
              }
              aria-live="off"
            />
          )}
        </div>
      </div>
    </>
  );
}

export const logUserAuswahl = (value: MAuswahlwert) => {
  const auswahlKey = value?.Auswahl?.Key;
  APP.logUserAction({ action: "auswahl", auswahlKey, value });
};
